import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { NavigationTabConfig } from "app/pages/sales-intelligence/modules/company-analysis/types/navigation-tabs";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const getSelectedTabs = (currentStateId: string | undefined, config: readonly NavigationTabConfig[]) => {
    if (!currentStateId) {
        return { tabIndex: -1, subTabIndex: -1 };
    }

    let tabIndex = -1;
    let subTabIndex = -1;

    config.forEach((item, i) => {
        if (tabIndex < 0 && item.stateId === currentStateId) {
            tabIndex = i;
        }
        if (item.items && subTabIndex < 0) {
            const idx = item.items.findIndex((si) => si.stateId === currentStateId);
            if (idx >= 0) {
                tabIndex = i;
                subTabIndex = idx;
            }
        }
    });

    return { tabIndex, subTabIndex };
};

const useTabNavigation = (
    config: readonly NavigationTabConfig[],
    onTabClickProp?: (tabName: string) => void,
    onSubTabClickProp?: (subTabName: string) => void,
) => {
    const { state, params } = useCurrentStateAndParams();

    const [{ tabIndex, subTabIndex }, setIndexes] = useState(() => getSelectedTabs(state.stateId, config));

    useEffect(() => {
        setIndexes(getSelectedTabs(state.stateId, config));
    }, [state.stateId, config]);

    const navigate = (stateId: string) => {
        void proNavigator.go(stateId, { key: params.key, isWWW: "*", duration: "3m", country: 999 });
    };

    const onTabClick = (index: number) => {
        if (index === tabIndex && !config[index].items) {
            return;
        }

        const stateId = config[index].stateId;
        setIndexes({ tabIndex: index, subTabIndex: -1 });
        navigate(stateId);
        onTabClickProp?.(config[index].trackingName ?? "");
    };

    const onSubTabClick = (stateId: string) => {
        const parentIndex = config.findIndex((item) => item.items?.some((si) => si.stateId === stateId));
        if (parentIndex === -1) {
            return;
        }
        const childIndex = config[parentIndex].items?.findIndex((si) => si.stateId === stateId) ?? -1;
        if (childIndex === -1 || (parentIndex === tabIndex && childIndex === subTabIndex)) {
            return;
        }
        setIndexes({ tabIndex: parentIndex, subTabIndex: childIndex });
        navigate(stateId);
        onSubTabClickProp?.(config[parentIndex].items?.[childIndex].trackingName ?? "");
    };

    return { tabIndex, subTabIndex, onTabClick, onSubTabClick };
};

export default useTabNavigation;
